import { CheckedState } from "@radix-ui/react-checkbox"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import { ListItemStack } from "@molecules"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"

import { Settings } from "@templates/index"

import {
    JobsManagerNotificationTopics,
    JobsNotificationTopics,
    NotificationMethods,
} from "@pages/Settings/SettingsNotifications/SettingsNotification.types"
import {
    notificationPreferenceQueryKey,
    useNotificationPreferencesQuery,
} from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import NotificationProperty from "@pages/Settings/SettingsNotifications/components/NotificationProperty/NotificationProperty"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

export default function JobsManagerNotificationsForm() {
    const [shouldEnableAllEmailNotifications, setShouldEnableAllEmailNotifications] = useState<boolean>(false)
    const [shouldDisableAllEmailNotifications, setShouldDisableAllEmailNotifications] = useState<boolean>(false)
    const [shouldEnableAllSmsNotifications, setShouldEnableAllSmsNotifications] = useState<boolean>(false)
    const [shouldDisableAllSmsNotifications, setShouldDisableAllSmsNotifications] = useState<boolean>(false)
    const [shouldSetDefaultFormData, setShouldSetDefaultFormData] = useState<boolean>(false)

    const { topicPreference: jobsNotificationPreference, isLoading: isLoadingData } =
        useNotificationPreferencesQuery<JobsNotificationTopics>("jobs")

    const queryClient = useQueryClient()

    useEffect(() => {
        if (!isLoadingData && !!jobsNotificationPreference) {
            setShouldSetDefaultFormData(true)
        }
    }, [isLoadingData])

    const handleToggleAllNotifications = (method: NotificationMethods, shouldEnableAllNotifications: CheckedState) => {
        if (method === "email") {
            setShouldDisableAllEmailNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllEmailNotifications(!!shouldEnableAllNotifications)
        } else {
            setShouldDisableAllSmsNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllSmsNotifications(!!shouldEnableAllNotifications)
        }
    }

    const modifyFieldset = (fieldset: JobsManagerNotificationTopics, method: NotificationMethods, value: boolean) => {
        return Object.keys(fieldset).reduce((acc, key) => {
            const notificationKey = key as keyof JobsManagerNotificationTopics
            if (key.endsWith(`_${method}`)) {
                acc[notificationKey] = value
            }
            return acc
        }, fieldset)
    }

    const formValues: JobsManagerNotificationTopics | undefined = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.manager, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.manager, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.manager, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.manager, "sms", false)
        } else if (shouldSetDefaultFormData) {
            return jobsNotificationPreference.manager
        } else {
            return undefined
        }
    }, [
        shouldSetDefaultFormData,
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const mutationTriggerPayload = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.manager, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.manager, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(jobsNotificationPreference.manager, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(jobsNotificationPreference.manager, "sms", false)
        } else {
            return undefined
        }
    }, [
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const resetNotificationStates = () => {
        setShouldEnableAllEmailNotifications(false)
        setShouldDisableAllEmailNotifications(false)
        setShouldEnableAllSmsNotifications(false)
        setShouldDisableAllSmsNotifications(false)
    }

    const onMutationSuccess = () => {
        void queryClient.invalidateQueries({ queryKey: notificationPreferenceQueryKey })
    }

    const onAfterFormValuesSet = () => {
        resetNotificationStates()
        setShouldSetDefaultFormData(false)
    }

    return (
        <GenericForm<
            JobsManagerNotificationTopics,
            JobsManagerNotificationTopics,
            Error,
            JobsManagerNotificationTopics
        >
            formConfig={{
                defaultValues: undefined,
            }}
            mutationConfig={{
                genericErrorMessage: "Could not update notification preference",
                genericSuccessMessage: "Notification preference updated",
                method: "PATCH",
                endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES,
            }}
            onMutationSuccess={onMutationSuccess}
            mutationTriggerPayload={mutationTriggerPayload}
            formValues={formValues}
            onAfterFormValuesSet={onAfterFormValuesSet}
            isAutoSave={true}
        >
            {({ form }) => {
                const formValues = form.getValues()

                const emailValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_email"))
                    .map(([, value]) => value)

                const smsValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_sms"))
                    .map(([, value]) => value)

                const isAllNotificationsEmailEnabled = emailValues.every(Boolean)
                const isAllNotificationsSmsEnabled = smsValues.every(Boolean)
                const isSomeNotificationsEmailEnabled = emailValues.some(Boolean)
                const isSomeNotificationsSmsEnabled = smsValues.some(Boolean)

                return (
                    <Settings.NotificationConfigPage.Wrapper>
                        <ListItemStack>
                            <NotificationProperty
                                label="Enable manager updates notifications"
                                isLoading={isLoadingData}
                                isControlled={false}
                                isEmailIndeterminate={
                                    isSomeNotificationsEmailEnabled && !isAllNotificationsEmailEnabled
                                }
                                isEmailChecked={isAllNotificationsEmailEnabled}
                                onEmailCheckedChange={(checked) => handleToggleAllNotifications("email", checked)}
                                isSmsIndeterminate={isSomeNotificationsSmsEnabled && !isAllNotificationsSmsEnabled}
                                isSmsChecked={isAllNotificationsSmsEnabled}
                                onSmsCheckedChange={(checked) => handleToggleAllNotifications("sms", checked)}
                            />
                        </ListItemStack>
                        <ListItemStack>
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job created"
                                supportText="Notify me when a teammate creates a job."
                                emailCheckboxName="enable_job_notification_new_job_request_email"
                                smsCheckboxName="enable_job_notification_new_job_request_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job started"
                                supportText="Notify me when someone starts a job."
                                emailCheckboxName="enable_job_notification_job_started_email"
                                smsCheckboxName="enable_job_notification_job_started_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job quick-paused"
                                supportText="Notify me when someone quick-pauses a job."
                                emailCheckboxName="enable_job_notification_job_paused_email"
                                smsCheckboxName="enable_job_notification_job_paused_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job placed on-hold"
                                supportText="Notify me when someone places on-hold a job."
                                emailCheckboxName="enable_job_notification_job_placed_on_hold_email"
                                smsCheckboxName="enable_job_notification_job_placed_on_hold_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job resumed"
                                supportText="Notify me when someone resumes a job."
                                emailCheckboxName="enable_job_notification_job_resumed_email"
                                smsCheckboxName="enable_job_notification_job_resumed_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job completed"
                                supportText="Notify me when someone completes a job."
                                emailCheckboxName="enable_job_notification_job_completed_email"
                                smsCheckboxName="enable_job_notification_job_completed_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job summary updated"
                                supportText="Notify me when someone updates the summary of a job."
                                emailCheckboxName="enable_job_notification_job_summary_updated_email"
                                smsCheckboxName="enable_job_notification_job_summary_updated_sms"
                                isLoading={isLoadingData}
                            />
                        </ListItemStack>
                    </Settings.NotificationConfigPage.Wrapper>
                )
            }}
        </GenericForm>
    )
}
